import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { api } from "@/lib/api";
import { ArrowLeft, Droplets, Wind, Users, Maximize, Zap, Settings } from "lucide-react";
import SEO from "@/components/SEO";
import { RecentlyViewed, recordRecentlyViewed } from "@/components/RecentlyViewed";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const { t, tp } = useLanguage();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setActiveImage(0);
    setProduct(null);
    setRelated([]);
    api.getProduct(slug).then(p => {
      if (ignore) return;
      setProduct(p);
      recordRecentlyViewed(p);
      api.getProducts({ series: p.series }).then(all => {
        if (ignore) return;
        setRelated(all.filter(x => x.slug !== slug).slice(0, 3));
      }).catch(() => {});
    }).catch(() => {}).finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [slug]);

  if (loading) return (
    <div className="pt-32 pb-20 min-h-screen">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl animate-pulse">
        <div className="h-6 bg-slate-200 w-32 mb-8" />
        <div className="grid lg:grid-cols-2 gap-12"><div className="aspect-[4/3] bg-slate-200" /><div className="space-y-4"><div className="h-8 bg-slate-200 w-1/2" /><div className="h-4 bg-slate-200 w-3/4" /></div></div>
      </div>
    </div>
  );

  if (!product) return (
    <div className="pt-32 pb-20 min-h-screen flex items-center justify-center">
      <p className="text-slate-500">Product not found</p>
    </div>
  );

  const backLink = product.series === "spa" ? "/spa-series" : "/jacuzzi-series";

  // Calculate Load per SQM in kN/m²
  const calculateLoadPerSQM = () => {
    if (!product.dry_weight || !product.water_capacity || !product.length_mm || !product.width_mm) return null;
    const totalWeight = product.dry_weight + product.water_capacity; // kg
    const footprintArea = (product.length_mm / 1000) * (product.width_mm / 1000); // m²
    const loadKgPerSqm = totalWeight / footprintArea; // kg/m²
    const loadKNPerSqm = loadKgPerSqm * 0.00980665; // kN/m²
    return loadKNPerSqm.toFixed(2);
  };

  const loadPerSQM = calculateLoadPerSQM();

  const specRows = [
    { label: t("products.capacity"), value: tp(product.capacity_text), icon: Users },
    { label: t("products.dimensions"), value: `${product.dimensions} mm`, icon: Maximize },
    product.water_capacity && { label: t("products.waterCapacity"), value: `${product.water_capacity} L`, icon: Droplets },
    product.dry_weight && { label: t("products.dryWeight"), value: `${product.dry_weight} kg`, icon: null },
    (product.dry_weight && product.water_capacity) && { label: t("products.totalWeight"), value: `${product.dry_weight + product.water_capacity} kg`, icon: null },
    loadPerSQM && { label: t("products.loadPerSQM"), value: `${loadPerSQM} kN/m²`, icon: null },
    product.electrical && { label: t("products.electrical"), value: tp(product.electrical), icon: Zap },
    (product.series?.toLowerCase() === 'jacuzzi') && { label: t("products.powerRequirement"), value: "1 kW", icon: Zap },
    { label: t("products.hydroJets"), value: product.hydro_jets, icon: Droplets },
    product.air_jets > 0 && { label: t("products.airJets"), value: product.air_jets, icon: Wind },
    { label: t("products.seating"), value: tp(product.seating_description), icon: Users },
  ].filter(Boolean);

  // Breadcrumb data for schema
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com";
  const breadcrumbs = [
    { name: "Home", url: baseUrl },
    { name: product.series === 'spa' ? "Spa Series" : "Jacuzzi Series", url: `${baseUrl}/${product.series === 'spa' ? 'spa-series' : 'jacuzzi-series'}` },
    { name: product.name, url: `${baseUrl}/${product.series === 'spa' ? 'spa-series' : 'jacuzzi-series'}/${slug}` }
  ];

  // Product schema data with enhanced properties
  const productSchemaData = {
    name: product.name,
    description: `${product.name} ${product.series === 'spa' ? 'spa' : 'jacuzzi'} - ${product.capacity_text}, ${product.dimensions} mm dimensions, ${product.hydro_jets} hydro jets. Premium hydrotherapy system with advanced features.`,
    image: (product.images && product.images.length > 0) ? product.images[0] : product.image_url,
    sku: product.slug?.toUpperCase(),
    category: product.series === 'spa' ? 'Spa Systems' : 'Compact Jacuzzis',
    specs: [
      { "@type": "PropertyValue", "name": "Capacity", "value": product.capacity_text },
      { "@type": "PropertyValue", "name": "Dimensions", "value": `${product.dimensions} mm` },
      { "@type": "PropertyValue", "name": "Hydro Jets", "value": String(product.hydro_jets) },
      product.air_jets > 0 && { "@type": "PropertyValue", "name": "Air Jets", "value": String(product.air_jets) },
      product.water_capacity && { "@type": "PropertyValue", "name": "Water Capacity", "value": `${product.water_capacity} L` },
      product.dry_weight && { "@type": "PropertyValue", "name": "Dry Weight", "value": `${product.dry_weight} kg` },
      loadPerSQM && { "@type": "PropertyValue", "name": "Load Per SQM", "value": `${loadPerSQM} kN/m²` }
    ].filter(Boolean)
  };

  return (
    <div data-testid="product-detail-page">
      <SEO 
        title={`${product.name} - ${product.series === 'spa' ? 'Spa' : 'Jacuzzi'} ${product.capacity_text}`}
        description={`${product.name} ${product.series === 'spa' ? 'spa' : 'jacuzzi'} - ${product.capacity_text}, ${product.dimensions} mm, ${product.hydro_jets} hydro jets. Premium hydrotherapy system from Pruva Jacuzzi Dubai.`}
        keywords={`${product.name}, ${product.series} dubai, ${product.capacity_text} jacuzzi, hydrotherapy spa, pruva ${product.series}, balcony plunge pool, rooftop spa`}
        canonical={`${baseUrl}/${product.series === 'spa' ? 'spa-series' : 'jacuzzi-series'}/${slug}`}
        type="product"
        image={(product.images && product.images.length > 0) ? product.images[0] : product.image_url}
        product={productSchemaData}
        breadcrumbs={breadcrumbs}
      />
      
      {/* Breadcrumb Navigation */}
      <nav className="pt-24 pb-0 md:pt-28 bg-white" aria-label="Breadcrumb">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <ol className="flex items-center gap-2 text-sm text-slate-500 mb-4">
            <li><Link to="/" className="hover:text-slate-800">{t("nav.home")}</Link></li>
            <li>/</li>
            <li><Link to={backLink} className="hover:text-slate-800">{product.series === 'spa' ? t("products.spaSeries") : t("products.jacuzziSeries")}</Link></li>
            <li>/</li>
            <li className="text-slate-800 font-medium">{product.name}</li>
          </ol>
          <Link to={backLink} data-testid="back-to-list"
            className="inline-flex items-center gap-2 text-sm text-slate-800 hover:text-slate-600 uppercase tracking-widest font-medium mb-8 transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" /> {t("products.backToList")}
          </Link>
        </div>
      </nav>

      {/* Product */}
      <section className="py-8 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Image Gallery */}
            <div>
              <div className="aspect-[4/3] overflow-hidden bg-slate-50 mb-3">
                <img src={(product.images && product.images.length > 0) ? product.images[activeImage] : product.image_url}
                  alt={`${product.name} ${product.series === 'spa' ? 'spa' : 'jacuzzi'} - ${product.capacity_text} capacity, ${product.hydro_jets} hydro jets - Pruva Dubai`} data-testid="product-image"
                  className="w-full h-full object-cover" />
              </div>
              {product.images && product.images.length > 1 && (
                <div className="flex gap-3" data-testid="product-thumbnails">
                  {product.images.map((img, i) => (
                    <button key={img} data-testid={`thumb-${i}`} onClick={() => setActiveImage(i)}
                      className={`w-20 h-20 overflow-hidden border-2 transition-colors duration-200 ${i === activeImage ? 'border-slate-800' : 'border-transparent hover:border-slate-300'}`}>
                      <img src={img} alt={`${product.name} ${product.series} view ${i + 1} - Pruva Jacuzzi Dubai`} loading="lazy" className="w-full h-full object-cover" width="100" height="100" decoding="async" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <span className="text-xs uppercase tracking-wider text-amber-700 bg-amber-50 px-3 py-1 font-medium">
                {product.series === "spa" ? t("products.spaSeries") : t("products.jacuzziSeries")}
              </span>
              <h1 data-testid="product-name" className="text-4xl md:text-5xl font-semibold text-slate-900 mt-4 mb-2">
                {product.name}
              </h1>
              <p className="text-lg text-slate-500 mb-8" style={{ fontFamily: "'Inter', sans-serif" }}>{tp(product.subtitle)}</p>

              {/* Key Stats */}
              <div className="grid grid-cols-3 gap-4 mb-10">
                <div className="text-center p-4 bg-slate-100 border border-slate-200">
                  <Droplets className="w-5 h-5 text-slate-800 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-2xl font-semibold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>{product.hydro_jets}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{t("products.hydroJets")}</p>
                </div>
                <div className="text-center p-4 bg-slate-100 border border-slate-200">
                  <Users className="w-5 h-5 text-slate-800 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-2xl font-semibold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>{product.capacity}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{t("products.persons")}</p>
                </div>
                <div className="text-center p-4 bg-slate-100 border border-slate-200">
                  <Maximize className="w-5 h-5 text-slate-800 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-lg font-semibold text-slate-800" dir="ltr" style={{ fontFamily: "'Inter', sans-serif" }}>{product.dimensions}</p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">mm</p>
                </div>
              </div>

              {/* Contact CTA */}
              <Link to="/contact" data-testid="product-contact-cta"
                className="inline-block bg-slate-800 text-white px-8 py-3.5 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors duration-300 mb-6">
                {t("hero.ctaContact")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 data-testid="specs-title" className="text-2xl md:text-3xl font-medium text-slate-900 mb-10">{t("products.specifications")}</h2>
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-0">
            {specRows.map((row, i) => (
              <div key={row.label} data-testid={`spec-row-${i}`} className="flex items-center justify-between py-4 border-b border-slate-200">
                <span className="text-sm text-slate-500 font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>{row.label}</span>
                <span className="text-sm text-slate-900 font-medium" style={{ fontFamily: "'Inter', sans-serif" }} dir={row.label === t("products.dimensions") ? "ltr" : undefined}>{row.value}</span>
              </div>
            ))}
          </div>

          {/* Pump System */}
          {product.pumps?.length > 0 && (
            <div className="mt-10">
              <h3 className="text-lg font-medium text-slate-900 mb-4">{t("products.pumpSystem")}</h3>
              <div className="flex flex-wrap gap-3">
                {product.pumps.map((pump) => (
                  <span key={pump} className="px-4 py-2 bg-white border border-slate-200 text-sm text-slate-700" style={{ fontFamily: "'Inter', sans-serif" }}>{tp(pump)}</span>
                ))}
              </div>
            </div>
          )}

          {/* Control & Filtration */}
          {product.control_system && (
            <div className="mt-8">
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t("products.controlSystem")}</h3>
              <p className="text-sm text-slate-600" style={{ fontFamily: "'Inter', sans-serif" }}>{tp(product.control_system)}</p>
            </div>
          )}
          {product.filtration && (
            <div className="mt-6">
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t("products.filtrationSystem")}</h3>
              <p className="text-sm text-slate-600" style={{ fontFamily: "'Inter', sans-serif" }}>{tp(product.filtration)}</p>
            </div>
          )}
          {/* Technical Images */}
          {product.technicalImages?.length > 0 && (
            <div className="mt-12" data-testid="technical-images">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {product.technicalImages.map((img, i) => (
                  <div key={img} className="bg-slate-900 p-4 flex items-center justify-center">
                    <img src={img} alt={`${product.name} - View ${i + 1}`} data-testid={`tech-img-${i}`} loading="lazy"
                      className="w-full h-auto object-contain max-h-48" width="400" height="400" decoding="async" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-8">{t("products.features")}</h2>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {/* Lighting */}
            {product.lighting?.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-3">{t("products.lightingSystem")}</h3>
                <ul className="space-y-1.5">
                  {product.lighting.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <span className="w-1.5 h-1.5 bg-slate-800 rounded-full mt-1.5 shrink-0" />
                      {tp(item)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Features */}
            {product.features?.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-3">{t("products.specialFeatures")}</h3>
                <ul className="space-y-1.5">
                  {product.features.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <span className="w-1.5 h-1.5 bg-amber-700 rounded-full mt-1.5 shrink-0" />
                      {tp(item)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Accessories */}
            {product.accessories?.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-3">{t("products.optionalAccessories")}</h3>
                <ul className="space-y-1.5">
                  {product.accessories.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-1.5 shrink-0" />
                      {tp(item)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Installation Considerations */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-6">{t("products.installConsiderations")}</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-slate-50 border border-slate-100">
              <h3 className="text-base font-semibold text-slate-900 mb-2">{t("products.spaceRequirements")}</h3>
              <p className="text-sm text-slate-600">
                {t("products.spaceReqText1")} <span dir="ltr">{product.length_mm + 200}mm x {product.width_mm + 200}mm</span> {t("products.spaceReqText2")}
              </p>
            </div>
            <div className="p-6 bg-slate-50 border border-slate-100">
              <h3 className="text-base font-semibold text-slate-900 mb-2">{t("products.structuralLoad")}</h3>
              <p className="text-sm text-slate-600">
                {loadPerSQM ? <><span dir="ltr">{t("products.structuralLoadText1")} {loadPerSQM} kN/m².</span>{" "}</> : ''}{t("products.structuralLoadText2")}
              </p>
            </div>
            <div className="p-6 bg-slate-50 border border-slate-100">
              <h3 className="text-base font-semibold text-slate-900 mb-2">{t("products.electricalSetup")}</h3>
              <p className="text-sm text-slate-600">
                <span dir="ltr">{product.electrical || (product.series === 'jacuzzi' ? t("products.electricalDefaultJacuzzi") : t("products.electricalDefault380"))}</span>. {t("products.electricalSetupText1")}
              </p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-slate-800 text-white px-8 py-3 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors duration-300">
              {t("products.requestAssessment")}
            </Link>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 md:py-24 bg-slate-50">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-10">{t("products.relatedProducts")}</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {related.map(p => (
                <Link key={p.slug} to={`/${p.series === 'spa' ? 'spa-series' : 'jacuzzi-series'}/${p.slug}`}
                  data-testid={`related-${p.slug}`}
                  className="group bg-white overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" width="400" height="400" decoding="async" />
                  </div>
                  <div className="p-5">
                    <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: "'Inter', sans-serif" }}>{p.name}</h3>
                    <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>{tp(p.subtitle)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <RecentlyViewed excludeSlug={slug} />
    </div>
  );
}
